import { API } from '../api.js';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import styles from './CalendarTab.module.css';

const MONTH_NAMES = ['January','February','March','April','May','June',
                     'July','August','September','October','November','December'];
const DAY_NAMES   = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];

// ── Calendar export helpers ────────────────────────────────────────────────────

function pad(n) { return String(n).padStart(2, '0'); }

function eventToDateStr(event) {
  const dt = new Date(event.eventDate);
  return `${dt.getFullYear()}${pad(dt.getMonth()+1)}${pad(dt.getDate())}`;
}

function buildGoogleCalLink(event) {
  const date  = eventToDateStr(event);
  const [sh, sm] = event.startTime.split(':');
  const [eh, em] = event.endTime.split(':');
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${date}T${pad(sh)}${pad(sm)}00/${date}T${pad(eh)}${pad(em)}00&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.link)}`;
}

function buildOutlookLink(event) {
  const dt    = new Date(event.eventDate);
  const base  = `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}`;
  return `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(event.title)}&startdt=${base}T${event.startTime}:00&enddt=${base}T${event.endTime}:00&body=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.link)}`;
}

function buildICS(event) {
  const date  = eventToDateStr(event);
  const [sh, sm] = event.startTime.split(':');
  const [eh, em] = event.endTime.split(':');
  return [
    'BEGIN:VCALENDAR','VERSION:2.0','BEGIN:VEVENT',
    `DTSTART:${date}T${pad(sh)}${pad(sm)}00`,
    `DTEND:${date}T${pad(eh)}${pad(em)}00`,
    `SUMMARY:${event.title}`,
    `DESCRIPTION:${(event.description||'').replace(/\n/g,'\\n')}`,
    `LOCATION:${event.link||''}`,
    'END:VEVENT','END:VCALENDAR',
  ].join('\r\n');
}

function downloadICS(event) {
  const blob = new Blob([buildICS(event)], { type: 'text/calendar' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = event.title.replace(/\s+/g,'_') + '.ics';
  a.click(); URL.revokeObjectURL(url);
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function daysInMonth(year, month) { return new Date(year, month, 0).getDate(); }

// weekday of the 1st (0=Mon…6=Sun for our Mon-first grid)
function firstWeekday(year, month) {
  const jsDay = new Date(year, month - 1, 1).getDay(); // 0=Sun
  return (jsDay + 6) % 7; // shift so 0=Mon
}

function accessColor(access) {
  if (access?.includes('Pro'))   return 'var(--accent)';
  if (access?.includes('Level')) return '#ffd700';
  return 'var(--accent3)';
}

const COLOR_OPTIONS = [
  '#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16',
];

const BLANK_FORM = {
  title: '', description: '', link: '',
  eventDate: '', startTime: '09:00', endTime: '10:00',
  timezone: 'UTC', color: '#6366f1', access: 'everyone', recurring: false,
};

// ── Main component ─────────────────────────────────────────────────────────────

export default function CalendarTab({ onToast, community, activeMembership, searchQuery = '' }) {
  const { user, token } = useAuth();

  const now   = new Date();
  const [year,  setYear]  = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-based
  const [events,  setEvents]  = useState([]);
  const [loading, setLoading] = useState(false);

  const [view, setView] = useState('calendar'); // 'calendar' | 'list'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEvent,    setEditingEvent]    = useState(null); // event being edited
  const [form, setForm] = useState(BLANK_FORM);
  const [saving, setSaving] = useState(false);

  const isOwner    = user && community && (Number(user.id) === Number(community.ownerId) || user.role === 'admin');
  const isCommAdmin = isOwner || activeMembership?.role === 'admin';
  const canManage  = isOwner; // only owner can create/edit/delete events

  // ── Fetch events for current month ──────────────────────────────────────────
  const fetchEvents = useCallback(async () => {
    if (!community?.id) return;
    setLoading(true);
    try {
      const res  = await fetch(`${API}/api/community/events?communityId=${community.id}&year=${year}&month=${month}`);
      const data = await res.json();
      if (Array.isArray(data)) setEvents(data);
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [community?.id, year, month]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  // ── Month navigation ─────────────────────────────────────────────────────────
  function prevMonth() {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  }
  function goToday() {
    setYear(now.getFullYear());
    setMonth(now.getMonth() + 1);
  }

  // ── Search filter ────────────────────────────────────────────────────────────
  const filtered = searchQuery.trim()
    ? events.filter(e => {
        const q = searchQuery.toLowerCase();
        return (e.title||'').toLowerCase().includes(q) || (e.description||'').toLowerCase().includes(q);
      })
    : events;

  // ── Build day→events map ─────────────────────────────────────────────────────
  const eventDayMap = {};
  filtered.forEach(e => {
    const days = e.recurring
      ? Array.from({ length: daysInMonth(year, month) }, (_, i) => i + 1)
      : [e.day];
    days.forEach(d => {
      if (!eventDayMap[d]) eventDayMap[d] = [];
      eventDayMap[d].push(e);
    });
  });

  // ── Build calendar grid ──────────────────────────────────────────────────────
  const totalDays  = daysInMonth(year, month);
  const startOffset = firstWeekday(year, month);
  const prevDays   = daysInMonth(year, month === 1 ? 12 : month - 1);
  const cells = [];

  for (let i = startOffset - 1; i >= 0; i--)
    cells.push({ day: prevDays - i, cur: false });
  for (let d = 1; d <= totalDays; d++)
    cells.push({ day: d, cur: true });
  while (cells.length % 7 !== 0)
    cells.push({ day: cells.length - totalDays - startOffset + 1, cur: false });

  // ── Create / Edit modal ───────────────────────────────────────────────────────
  function openCreate() {
    setEditingEvent(null);
    setForm({ ...BLANK_FORM, eventDate: `${year}-${pad(month)}-01` });
    setShowCreateModal(true);
  }
  function openEdit(ev) {
    setEditingEvent(ev);
    const dt = new Date(ev.eventDate);
    setForm({
      title:       ev.title,
      description: ev.description || '',
      link:        ev.link || '',
      eventDate:   `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}`,
      startTime:   ev.startTime,
      endTime:     ev.endTime,
      timezone:    ev.timezone || 'UTC',
      color:       ev.color || '#6366f1',
      access:      ev.access || 'everyone',
      recurring:   ev.recurring || false,
    });
    setSelectedEvent(null);
    setShowCreateModal(true);
  }

  async function saveEvent() {
    if (!form.title.trim()) return onToast('Title is required');
    if (!form.eventDate)    return onToast('Event date is required');
    setSaving(true);
    try {
      const payload = { ...form, communityId: community.id };
      const url    = editingEvent
        ? `${API}/api/community/events/${editingEvent.id}`
        : `${API}/api/community/events`;
      const method = editingEvent ? 'PATCH' : 'POST';
      const res    = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        onToast(err.error || 'Failed to save event');
        return;
      }
      onToast(editingEvent ? 'Event updated' : 'Event created');
      setShowCreateModal(false);
      fetchEvents();
    } finally {
      setSaving(false);
    }
  }

  async function deleteEvent(ev) {
    if (!window.confirm(`Delete "${ev.title}"?`)) return;
    const res = await fetch(`${API}/api/community/events/${ev.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      onToast('Event deleted');
      setSelectedEvent(null);
      fetchEvents();
    } else {
      onToast('Failed to delete event');
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex-col">
      {/* Search result banner */}
      {searchQuery.trim() && (
        <div style={{ background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:12, padding:'12px 16px', marginBottom:16, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span style={{ fontSize:13, color:'var(--text2)' }}>
            Events matching <strong>"{searchQuery}"</strong> ({filtered.length} found)
          </span>
        </div>
      )}

      <div className="card" style={{ borderRadius:12 }}>
        {/* Header */}
        <div className={styles.calHeader}>
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <button className="btn btn-ghost btn-sm" style={{ border:'1px solid var(--border)' }} onClick={goToday}>Today</button>
            {canManage && (
              <button className="btn btn-sm" style={{ background:'var(--accent)', color:'#fff', border:'none', fontWeight:700 }} onClick={openCreate}>
                + Event
              </button>
            )}
          </div>

          <div className={styles.calMonthSelector}>
            <button className="icon-btn" style={{ border:'none' }} onClick={prevMonth}>◀</button>
            <div className="flex-col" style={{ alignItems:'center' }}>
              <div style={{ fontWeight:700 }}>{MONTH_NAMES[month-1]} {year}</div>
              <div className={styles.calMonthSubtitle}>GMT+7:00 · Bangkok</div>
            </div>
            <button className="icon-btn" style={{ border:'none' }} onClick={nextMonth}>▶</button>
          </div>

          <div style={{ display:'flex', border:'1px solid var(--border)', borderRadius:8, overflow:'hidden' }}>
            <button
              className="btn btn-ghost btn-sm"
              style={{ borderRadius:0, padding:'6px 10px', background: view === 'list' ? 'var(--accent-soft)' : 'var(--surface2)', color: view === 'list' ? 'var(--accent)' : 'var(--text2)' }}
              onClick={() => setView('list')}
              title="List view"
            >≡</button>
            <button
              className="btn btn-ghost btn-sm"
              style={{ borderRadius:0, padding:'6px 10px', borderLeft:'1px solid var(--border)', background: view === 'calendar' ? 'var(--accent-soft)' : 'transparent', color: view === 'calendar' ? 'var(--accent)' : 'var(--text2)' }}
              onClick={() => setView('calendar')}
              title="Calendar view"
            >📅</button>
          </div>
        </div>

        {/* ── Calendar grid ── */}
        {view === 'calendar' && (
          <div className={styles.monthGrid}>
            {DAY_NAMES.map(d => <div key={d} className={styles.calDayName}>{d}</div>)}
            {cells.map((cell, i) => {
              const dayEvs = cell.cur ? (eventDayMap[cell.day] || []) : [];
              const isToday = cell.cur
                && cell.day === now.getDate()
                && month === now.getMonth() + 1
                && year  === now.getFullYear();
              return (
                <div key={i} className={`${styles.calDay} ${!cell.cur ? styles.otherMonth : ''}`}>
                  <div style={{ marginBottom:4 }}>
                    <span className={isToday ? styles.today : ''}>{cell.day}</span>
                  </div>
                  {dayEvs.slice(0, 2).map(ev => (
                    <div key={ev.id + '-' + cell.day} className={styles.eventPill}
                      style={{ background:ev.color+'22', color:ev.color, borderLeft:`3px solid ${ev.color}`, cursor:'pointer', fontSize:10, padding:'2px 5px', borderRadius:3, marginBottom:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}
                      onClick={() => setSelectedEvent(ev)}>
                      {ev.startTime} {ev.title}
                    </div>
                  ))}
                  {dayEvs.length > 2 && (
                    <div style={{ fontSize:10, color:'var(--text3)', paddingLeft:4, cursor:'pointer' }}
                      onClick={() => setSelectedEvent(dayEvs[2])}>
                      +{dayEvs.length - 2} more
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── List view ── */}
        {view === 'list' && (
          <div className={styles.listView}>
            {filtered.length === 0 && !loading && (
              <div className={styles.listEmpty}>
                <span style={{ fontSize:32 }}>📭</span>
                <div>No events this month</div>
                {canManage && <div style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>Click + Event to schedule one</div>}
              </div>
            )}
            {(() => {
              // Group unique events by day (deduplicate recurring, which appear once per day in the map)
              const uniqueEvents = [...new Map(filtered.map(e => [e.id, e])).values()];
              const byDay = {};
              uniqueEvents.forEach(e => {
                if (e.recurring) {
                  for (let d = 1; d <= daysInMonth(year, month); d++) {
                    if (!byDay[d]) byDay[d] = [];
                    byDay[d].push(e);
                  }
                } else {
                  if (!byDay[e.day]) byDay[e.day] = [];
                  byDay[e.day].push(e);
                }
              });
              return Object.keys(byDay).sort((a,b) => +a - +b).map(d => {
                const dayNum  = +d;
                const date    = new Date(year, month - 1, dayNum);
                const isToday = dayNum === now.getDate() && month === now.getMonth()+1 && year === now.getFullYear();
                const dayLabel = date.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' });
                return (
                  <div key={d} className={styles.listDayGroup}>
                    <div className={styles.listDayHeader}>
                      <span className={`${styles.listDayNum} ${isToday ? styles.listDayNumToday : ''}`}>{dayNum}</span>
                      <span className={styles.listDayLabel}>{dayLabel}</span>
                      {isToday && <span className={styles.listTodayBadge}>Today</span>}
                    </div>
                    {byDay[d].map(ev => (
                      <div key={ev.id} className={styles.listEventRow} onClick={() => setSelectedEvent(ev)}>
                        <div className={styles.listEventColor} style={{ background: ev.color }} />
                        <div className={styles.listEventTime}>{ev.startTime} – {ev.endTime}</div>
                        <div className={styles.listEventInfo}>
                          <div className={styles.listEventTitle}>{ev.title}</div>
                          {ev.description && <div className={styles.listEventDesc}>{ev.description}</div>}
                        </div>
                        <div className={styles.listEventAccess} style={{ background: accessColor(ev.access)+'18', color: accessColor(ev.access) }}>
                          {ev.access}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              });
            })()}
          </div>
        )}

        {loading && (
          <div style={{ padding:'12px 20px', fontSize:12, color:'var(--text3)', textAlign:'center' }}>Loading…</div>
        )}
      </div>

      {/* ── Event Detail Modal ── */}
      {selectedEvent && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setSelectedEvent(null)}>
          <div className="modal" style={{ maxWidth:480, padding:0, borderRadius:14, overflow:'hidden' }}>
            <div style={{ height:8, background:selectedEvent.color }} />
            <div style={{ padding:'24px 28px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
                <h2 style={{ fontSize:20, fontWeight:800, color:'var(--text)', margin:0, flex:1, paddingRight:16 }}>
                  {selectedEvent.title}
                </h2>
                <button onClick={() => setSelectedEvent(null)}
                  style={{ background:'none', border:'none', cursor:'pointer', fontSize:20, color:'var(--text3)', lineHeight:1 }}>×</button>
              </div>

              <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20 }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontSize:16 }}>🕐</span>
                  <div>
                    <div style={{ fontSize:14, fontWeight:600, color:'var(--text)' }}>
                      {(() => { const dt = new Date(selectedEvent.eventDate); return `${MONTH_NAMES[dt.getMonth()]} ${dt.getDate()}, ${dt.getFullYear()}`; })()}
                      {' '}· {selectedEvent.startTime} – {selectedEvent.endTime}
                    </div>
                    <div style={{ fontSize:12, color:'var(--text3)' }}>{selectedEvent.timezone}</div>
                  </div>
                </div>

                {selectedEvent.recurring && (
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <span style={{ fontSize:16 }}>🔁</span>
                    <span style={{ fontSize:13, color:'var(--text2)' }}>Repeats daily</span>
                  </div>
                )}

                {selectedEvent.link && (
                  <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <span style={{ fontSize:16 }}>🔗</span>
                    <a href={selectedEvent.link} target="_blank" rel="noreferrer"
                      style={{ fontSize:13, color:'var(--accent)', textDecoration:'underline' }}>
                      {selectedEvent.link}
                    </a>
                  </div>
                )}

                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <span style={{ fontSize:16 }}>🔑</span>
                  <span style={{ fontSize:12, background:accessColor(selectedEvent.access)+'22', color:accessColor(selectedEvent.access), padding:'3px 10px', borderRadius:10, fontWeight:600 }}>
                    {selectedEvent.access}
                  </span>
                </div>
              </div>

              {selectedEvent.description && (
                <div style={{ background:'var(--surface2)', borderRadius:10, padding:'14px 16px', marginBottom:24 }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'var(--text3)', marginBottom:6, textTransform:'uppercase', letterSpacing:0.5 }}>About this event</div>
                  <p style={{ fontSize:13, color:'var(--text2)', lineHeight:1.6, margin:0 }}>{selectedEvent.description}</p>
                </div>
              )}

              <div>
                <div style={{ fontSize:12, fontWeight:700, color:'var(--text3)', marginBottom:10, textTransform:'uppercase', letterSpacing:0.5 }}>Add to Calendar</div>
                <div style={{ display:'flex', gap:8 }}>
                  <a href={buildGoogleCalLink(selectedEvent)} target="_blank" rel="noreferrer"
                    style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'10px 12px', borderRadius:8, border:'1px solid var(--border)', background:'var(--surface2)', fontSize:12, fontWeight:600, color:'var(--text)', cursor:'pointer', textDecoration:'none' }}>
                    📅 Google
                  </a>
                  <button onClick={() => downloadICS(selectedEvent)}
                    style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'10px 12px', borderRadius:8, border:'1px solid var(--border)', background:'var(--surface2)', fontSize:12, fontWeight:600, color:'var(--text)', cursor:'pointer' }}>
                    🍎 Apple
                  </button>
                  <a href={buildOutlookLink(selectedEvent)} target="_blank" rel="noreferrer"
                    style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6, padding:'10px 12px', borderRadius:8, border:'1px solid var(--border)', background:'var(--surface2)', fontSize:12, fontWeight:600, color:'var(--text)', cursor:'pointer', textDecoration:'none' }}>
                    📧 Outlook
                  </a>
                </div>
              </div>

              <div style={{ display:'flex', gap:10, marginTop:20 }}>
                {canManage && (
                  <>
                    <button onClick={() => openEdit(selectedEvent)}
                      style={{ padding:'10px 18px', borderRadius:8, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', cursor:'pointer', fontSize:13, fontWeight:600 }}>
                      Edit
                    </button>
                    <button onClick={() => deleteEvent(selectedEvent)}
                      style={{ padding:'10px 18px', borderRadius:8, border:'1px solid #ef444430', background:'#ef444410', color:'#ef4444', cursor:'pointer', fontSize:13, fontWeight:600 }}>
                      Delete
                    </button>
                  </>
                )}
                <button onClick={() => setSelectedEvent(null)}
                  style={{ flex:1, padding:'11px', borderRadius:8, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', cursor:'pointer', fontSize:13, fontWeight:600 }}>
                  Close
                </button>
                <button onClick={() => { setSelectedEvent(null); onToast('✓ RSVP confirmed! Check your calendar.'); }}
                  style={{ flex:2, padding:'11px', borderRadius:8, border:'none', background:'var(--accent)', color:'#fff', cursor:'pointer', fontSize:13, fontWeight:700 }}>
                  RSVP · Join Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Create / Edit Event Modal — Premium Redesign ── */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCreateModal(false)}>
          <div className={styles.eventModal}>

            {/* ── Header ── */}
            <div className="modal-header">
              <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:'var(--text)' }}>
                {editingEvent ? 'Edit Event' : 'New Event'}
              </h3>
              <button onClick={() => setShowCreateModal(false)}
                style={{ background:'var(--surface2)', border:'none', borderRadius:'50%', width:30, height:30, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', color:'var(--text2)' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {/* ── Scrollable body ── */}
            <div className={styles.eventModalBody}>

              {/* Section: Basic Info */}
              <div className={styles.eventSection}>
                <div className={styles.eventSectionLabel}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                  Basic Info
                </div>

                <div className={styles.eventFieldGroup}>
                  <label className={styles.eventLabel}>
                    <span>Title</span><span className={styles.eventRequired}>*</span>
                  </label>
                  <input
                    className={`input ${styles.eventInput}`}
                    value={form.title}
                    onChange={e => setForm(f=>({...f,title:e.target.value}))}
                    placeholder="Give your event a name…"
                  />
                </div>

                <div className={styles.eventFieldGroup}>
                  <label className={styles.eventLabel}>Description</label>
                  <textarea
                    className={`input ${styles.eventInput}`}
                    rows={3}
                    value={form.description}
                    onChange={e => setForm(f=>({...f,description:e.target.value}))}
                    placeholder="What will attendees experience? Share details, agenda, or any prep needed…"
                    style={{ resize:'vertical' }}
                  />
                </div>

                <div className={styles.eventFieldGroup}>
                  <label className={styles.eventLabel}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:4}}><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                    Link / Location
                  </label>
                  <input
                    className={`input ${styles.eventInput}`}
                    value={form.link}
                    onChange={e => setForm(f=>({...f,link:e.target.value}))}
                    placeholder="https://zoom.us/… or venue address"
                  />
                </div>
              </div>

              <div className={styles.eventDivider} />

              {/* Section: Date & Time */}
              <div className={styles.eventSection}>
                <div className={styles.eventSectionLabel}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  Date & Time
                </div>

                <div className={styles.eventTimeGrid}>
                  <div className={styles.eventFieldGroup} style={{ gridColumn:'1/-1' }}>
                    <label className={styles.eventLabel}>Date <span className={styles.eventRequired}>*</span></label>
                    <input
                      className={`input ${styles.eventInput}`}
                      type="date"
                      value={form.eventDate}
                      onChange={e => setForm(f=>({...f,eventDate:e.target.value}))}
                    />
                  </div>
                  <div className={styles.eventFieldGroup}>
                    <label className={styles.eventLabel}>Start time</label>
                    <input
                      className={`input ${styles.eventInput}`}
                      type="time"
                      value={form.startTime}
                      onChange={e => setForm(f=>({...f,startTime:e.target.value}))}
                    />
                  </div>
                  <div className={styles.eventFieldGroup}>
                    <label className={styles.eventLabel}>End time</label>
                    <input
                      className={`input ${styles.eventInput}`}
                      type="time"
                      value={form.endTime}
                      onChange={e => setForm(f=>({...f,endTime:e.target.value}))}
                    />
                  </div>
                  <div className={styles.eventFieldGroup} style={{ gridColumn:'1/-1' }}>
                    <label className={styles.eventLabel}>Timezone</label>
                    <input
                      className={`input ${styles.eventInput}`}
                      value={form.timezone}
                      onChange={e => setForm(f=>({...f,timezone:e.target.value}))}
                      placeholder="e.g. UTC, Asia/Bangkok, America/New_York"
                    />
                  </div>
                </div>

                {/* Recurring toggle */}
                <div className={styles.eventRecurring} onClick={() => setForm(f=>({...f,recurring:!f.recurring}))}>
                  <div>
                    <div className={styles.eventRecurringTitle}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{marginRight:6}}><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
                      Recurring event
                    </div>
                    <div className={styles.eventRecurringSub}>Show on every day of the month</div>
                  </div>
                  <div className={`${styles.toggle} ${form.recurring ? styles.toggleOn : ''}`} style={form.recurring ? { background: form.color } : {}}>
                    <div className={styles.toggleKnob} />
                  </div>
                </div>
              </div>

              <div className={styles.eventDivider} />

              {/* Section: Access & Appearance */}
              <div className={styles.eventSection}>
                <div className={styles.eventSectionLabel}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                  Access & Appearance
                </div>

                <div className={styles.eventFieldGroup}>
                  <label className={styles.eventLabel}>Who can see this event?</label>
                  <div className={styles.accessGrid}>
                    {[
                      { value:'everyone',    label:'Everyone',     icon:'🌍', desc:'Public to all visitors' },
                      { value:'members',     label:'Members',      icon:'👥', desc:'Logged-in members only' },
                      { value:'Pro members', label:'Pro Members',  icon:'⭐', desc:'Paid subscribers only'  },
                    ].map(opt => (
                      <div
                        key={opt.value}
                        className={`${styles.accessCard} ${form.access === opt.value ? styles.accessCardActive : ''}`}
                        style={form.access === opt.value ? { borderColor: form.color, background: form.color + '12' } : {}}
                        onClick={() => setForm(f=>({...f,access:opt.value}))}
                      >
                        <span className={styles.accessCardIcon}>{opt.icon}</span>
                        <div>
                          <div className={styles.accessCardLabel}>{opt.label}</div>
                          <div className={styles.accessCardDesc}>{opt.desc}</div>
                        </div>
                        {form.access === opt.value && (
                          <div className={styles.accessCheck} style={{ background: form.color }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className={styles.eventFieldGroup}>
                  <label className={styles.eventLabel}>Event color</label>
                  <div className={styles.colorSwatches}>
                    {COLOR_OPTIONS.map(c => (
                      <button
                        key={c}
                        className={`${styles.colorSwatch} ${form.color === c ? styles.colorSwatchActive : ''}`}
                        style={{ background: c, boxShadow: form.color === c ? `0 0 0 3px ${c}44, 0 0 0 5px ${c}22` : 'none' }}
                        onClick={() => setForm(f=>({...f,color:c}))}
                        title={c}
                      >
                        {form.color === c && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        )}
                      </button>
                    ))}
                    <div className={styles.colorHex} style={{ borderColor: form.color + '66', color: form.color }}>
                      {form.color}
                    </div>
                  </div>
                </div>
              </div>

            </div>{/* end body */}

            {/* ── Footer ── */}
            <div className={styles.eventModalFooter}>
              <button
                onClick={() => setShowCreateModal(false)}
                className={styles.eventBtnCancel}
              >
                Cancel
              </button>
              <button
                onClick={saveEvent}
                disabled={saving}
                className={styles.eventBtnSave}
                style={{ background: `linear-gradient(135deg, ${form.color} 0%, ${form.color}cc 100%)` }}
              >
                {saving ? (
                  <span className={styles.eventSavingDots}>
                    <span/><span/><span/>
                  </span>
                ) : (
                  <>
                    {editingEvent ? (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>
                    ) : (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    )}
                    {editingEvent ? 'Save Changes' : 'Create Event'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
